import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';

import { getErrorMessage } from '@/hooks/useApi';

import { CreateStoryInput, useCreateStory, useStories } from './useStories';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'initializing', label: 'Initializing' },
  { value: 'ready', label: 'Ready' },
  { value: 'failed', label: 'Failed' },
];

const GENRE_OPTIONS = [
  { value: 'fantasy', label: 'Fantasy' },
  { value: 'horror', label: 'Horror' },
  { value: 'mystery', label: 'Mystery' },
  { value: 'sci-fi', label: 'Sci-Fi' },
  { value: 'romance', label: 'Romance' },
];

export function StoryListPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data: stories, isLoading, error } = useStories(statusFilter || undefined);
  const createStory = useCreateStory();

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Stories</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
        >
          Create Story
        </button>
      </div>

      {/* Filter */}
      <div className="mb-6">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-slate-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading stories...</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="rounded-lg bg-red-50 p-4 text-red-700">{getErrorMessage(error)}</div>
      )}

      {/* Stories table */}
      {stories && stories.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                  Genre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                  Nodes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                  Plays
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {stories.map((story) => (
                <tr key={story.storyId} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <Link
                      to={`/stories/${story.storyId}`}
                      className="font-medium text-indigo-600 hover:text-indigo-800"
                    >
                      {story.title}
                    </Link>
                    <p className="mt-1 text-sm text-slate-500 truncate max-w-xs">
                      {story.description}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{story.genre}</td>
                  <td className="px-6 py-4">
                    <StatusBadge status={story.status} />
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{story.totalNodes}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{story.totalPlays}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty state */}
      {stories && stories.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
          <p className="text-slate-600">No stories found</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 text-indigo-600 hover:text-indigo-800"
          >
            Create your first story
          </button>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateStoryModal
          onClose={() => setShowCreateModal(false)}
          onCreate={async (input) => {
            await createStory.mutateAsync(input);
            setShowCreateModal(false);
          }}
          isLoading={createStory.isPending}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    initializing: 'bg-yellow-100 text-yellow-700',
    ready: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
  };

  return (
    <span
      className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${colors[status] || 'bg-slate-100 text-slate-700'}`}
    >
      {status}
    </span>
  );
}

function CreateStoryModal({
  onClose,
  onCreate,
  isLoading,
}: {
  onClose: () => void;
  onCreate: (input: CreateStoryInput) => Promise<void>;
  isLoading: boolean;
}) {
  const [title, setTitle] = useState('');
  const [genre, setGenre] = useState(GENRE_OPTIONS[0].value);
  const [description, setDescription] = useState('');
  const [setting, setSetting] = useState('');
  const [protagonistName, setProtagonistName] = useState('');
  const [protagonistTraits, setProtagonistTraits] = useState('');
  const [currentTension, setCurrentTension] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const traits = protagonistTraits
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    if (traits.length === 0) {
      setError('At least one protagonist trait is required');
      return;
    }
    try {
      await onCreate({
        title,
        genre,
        description,
        initialState: {
          setting,
          protagonist: {
            name: protagonistName,
            traits,
          },
          currentTension,
        },
      });
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-xl rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-slate-900 mb-4">Create Story</h2>

        {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
              placeholder="Enter story title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">Genre</label>
            <select
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
            >
              {GENRE_OPTIONS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={3}
              maxLength={500}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
              placeholder="Brief description of the story"
            />
          </div>

          <hr className="border-slate-200" />
          <h3 className="text-sm font-semibold text-slate-800">Initial Story State</h3>

          <div>
            <label className="block text-sm font-medium text-slate-700">Setting</label>
            <input
              type="text"
              value={setting}
              onChange={(e) => setSetting(e.target.value)}
              required
              maxLength={200}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
              placeholder="A dark medieval kingdom..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">Protagonist Name</label>
            <input
              type="text"
              value={protagonistName}
              onChange={(e) => setProtagonistName(e.target.value)}
              required
              maxLength={100}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
              placeholder="Kira"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">Protagonist Traits</label>
            <input
              type="text"
              value={protagonistTraits}
              onChange={(e) => setProtagonistTraits(e.target.value)}
              required
              className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
              placeholder="brave, clever, resourceful"
            />
            <p className="mt-1 text-xs text-slate-500">Comma-separated list of traits</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">Current Tension</label>
            <textarea
              value={currentTension}
              onChange={(e) => setCurrentTension(e.target.value)}
              required
              rows={2}
              maxLength={300}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
              placeholder="An ancient evil stirs beneath the castle..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:bg-indigo-400"
            >
              {isLoading ? 'Creating...' : 'Create Story'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
